"use client";

import React from "react";

import DecorationLine from "@/components/global/ui/DecorationLine";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableTextBlock } from "sanity";
import { GROQImage } from "@/sanity/lib/types";
import { II18nRichtext, resolveI18nRichtext } from "@/sanity/globals/I18n";

export default function Testimonials({
  text,
  testimonials,
  language,
  className = "",
}: {
  text?: PortableTextBlock[];
  testimonials?: {
    image: GROQImage;
    name: string;
    position: string;
    text: PortableTextBlock[];
    textI18n?: II18nRichtext;
  }[];
  language: "en" | "de";
  className?: string;
}) {
  if (!testimonials || testimonials.length === 0) return null;

  const testimonialColors = ["#D9F9B9", "#B9C0F9", "#F9E0B9", "#E9B9F9"];

  const columns = Array.from({ length: 4 }, (_, i) =>
    testimonials.filter((_, idx) => idx % 4 === i),
  );

  return (
    <div
      className={cn(
        "relative overflow-clip pb-[13rem] lg:pb-[18rem]",
        className,
      )}
    >
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />

      <div className="container lg:max-w-[204rem] relative flex flex-col items-center">
        {text && (
          <div className="richtext prose-p:w-[min(60rem,100%)] prose-headings:w-[min(120rem,100%)] mb-[4rem]">
            <PortableText value={text} components={richTextComponent} />
          </div>
        )}

        <div className="flex gap-[2rem] w-full h-[95.75rem] max-h-[85vh] overflow-hidden relative">
          {columns.map((col, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-full sm:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)] relative overflow-hidden"
            >
              <div
                className="flex flex-col gap-[2rem]"
                style={{
                  animation: `${i % 2 === 0 ? "scrollUp" : "scrollDown"} ${col.length * 5}s linear infinite`,
                  willChange: "transform",
                }}
              >
                {[...col, ...col].map((item, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 px-[2.5rem] pt-[3rem] md:px-[3rem] lg:px-[4rem] lg:pt-[4rem] bg-white"
                    style={{
                      borderBottom: `2px solid ${testimonialColors[index % testimonialColors.length]}`,
                    }}
                  >
                    <svg
                      className="w-[4.25rem] opacity-50 mb-[6rem] lg:mb-[10rem]"
                      viewBox="0 0 34 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20.5468 32L26.4173 13.4424H20.1798V0H34V11.777L26.6619 32H20.5468ZM0.366861 32L6.23741 13.4424H0V0H13.8202V11.777L6.48203 32H0.366861Z"
                        fill={
                          testimonialColors[index % testimonialColors.length]
                        }
                      />
                    </svg>

                    {resolveI18nRichtext(
                      item?.text,
                      item?.textI18n,
                      language,
                    ) && (
                      <div className="richtext richtext-start prose-p:leading-[1.3] prose-p:text-[2.5rem] lg:prose-p:text-[3rem] prose-p:text-black/70 mb-[3rem] lg:mb-[4rem]">
                        <PortableText
                          value={
                            resolveI18nRichtext(
                              item?.text,
                              item?.textI18n,
                              language,
                            )!
                          }
                          components={richTextComponent}
                        />
                      </div>
                    )}

                    <div
                      className={cn(
                        "inline-flex gap-[1rem] px-[1.5rem] pb-[1rem] pt-[calc(1rem+2px)] font-geistmono",
                        "[clip-path:polygon(1.5rem_0,0_1.5rem,0_100%,100%_100%,100%_1.5rem,calc(100%-1.5rem)_0)]",
                      )}
                      style={{
                        background:
                          testimonialColors[index % testimonialColors.length],
                      }}
                    >
                      <p className="mb-0 leading-none uppercase text-[2rem] font-medium text-black">
                        {item?.name?.replace(/[^a-zA-Z]/g, "").slice(0, 2) ||
                          ""}
                      </p>

                      <div className="w-[2px] bg-[#0A1A1D]/20" />

                      <p className="mb-0 leading-none text-black font-medium text-[2rem]">
                        {item?.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="absolute top-0 left-0 w-full h-[20rem] bg-gradient-to-b from-[#F3F2F1]" />
          <div className="absolute bottom-0 left-0 w-full h-[20rem] bg-gradient-to-t from-[#F3F2F1]" />
        </div>
      </div>
    </div>
  );
}
